import classNames from 'classnames'
import { ChildrenWithProps } from 'components/abstract/ChildrenWithProps'
import { Option } from 'components/common/Option'
import { StaticIcon } from 'components/common/StaticIcon'
import { useCloseMenu } from 'hooks/useCloseMenu'
import {
  Children,
  ComponentProps,
  FocusEventHandler,
  isValidElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { tw } from '../../utils/strings'

export type SelectProps = {
  id?: string
  className?: string
  children?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  before?: string
  after?: string
  label?: string | React.ReactNode
  value?: string
  placeholder?: string
  iconClassName?: string
  onChange?: (value: string) => void
  onBlur?: FocusEventHandler
  inputClassName?: string
  truncateTextAt?: number
  contentClassName?: string
  keepPlaceholder?: boolean
  triggerTextClassName?: string
  pill?: boolean
  trigger?: ReactNode
  variant?: 'default' | 'gray-bg'
  caption?: ReactNode
  labelClassName?: string
}

export const Select = ({
  id,
  size = 'md',
  before = '',
  after = '',
  label,
  value = '',
  placeholder = 'Select',
  iconClassName,
  onChange,
  className,
  children,
  inputClassName = '',
  truncateTextAt = 0,
  contentClassName,
  keepPlaceholder,
  triggerTextClassName,
  pill,
  trigger,
  variant = 'default',
  caption,
  labelClassName,
}: SelectProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const [valueLabel, setValueLabel] = useState('')
  const [internalValue, setInternalValue] = useState(value)

  const { ref, width } = useResizeDetector({
    handleWidth: Boolean(truncateTextAt),
  })

  useEffect(() => {
    if (value === internalValue) return
    setInternalValue(value || '')
  }, [internalValue, value])

  useEffect(() => {
    if (!children) return
    Children.map(children, child => {
      if (isValidElement(child)) {
        if (child.props.value === internalValue) {
          setValueLabel(child.props.children.toString())
        }
      }
    })
  }, [children, internalValue])

  useCloseMenu({
    closeHandler: () => setIsMenuOpen(false),
    isOpen: isMenuOpen,
    ref: containerRef,
  })

  const styles = {
    base: tw`w-full flex px-4 space-x-3 items-center justify-between rounded text-cool-900 placeholder-cool-500 focus:outline-none disabled:bg-cool-400 disabled:text-cool-600    transition-colors duration-200`,

    default:
      'bg-cool-50 border border-cool-400 hover:border-cool-500 focus:border-cool-900 disabled:border-cool-400',
    'gray-bg': 'bg-warm-100',

    sm: tw`!text-sm h-8 px-3`,
    md: tw`!text-sm h-10 px-3`,
    lg: tw`!text-base h-12 px-4`,
  }

  const getButtonText = () => {
    if (keepPlaceholder) return placeholder

    if (valueLabel) return `${before}${valueLabel}${after}`

    return placeholder
  }

  const triggerProps = {
    type: 'button' as const,
    id: id ? id + '-trigger' : undefined,
    onClick: () => setIsMenuOpen(!isMenuOpen),
    'aria-haspopup': 'listbox' as const,
    'aria-controls': id ? id + '-listbox' : undefined,
    ref,
  }

  return (
    <div className={` relative w-full ${className}`} ref={containerRef}>
      {label && (
        <label
          className={classNames(
            labelClassName,
            'flex justify-between text-sm font-medium',
            !caption && 'mb-1'
          )}
          htmlFor={id}
        >
          {label}
        </label>
      )}

      {caption && (
        <span className='mb-2 block text-xs text-cool-600'>{caption}</span>
      )}

      {!trigger && (
        <button
          {...triggerProps}
          title={getButtonText()}
          className={classNames(
            'relative',
            styles.base,
            styles[size],
            styles[variant],
            inputClassName,
            pill && 'rounded-full'
          )}
        >
          <span
            className={`block text-start ${
              truncateTextAt && 'truncate'
            } ${triggerTextClassName} inline-block w-[calc(100%)] overflow-hidden text-ellipsis whitespace-nowrap`}
            style={
              width && truncateTextAt && width < truncateTextAt
                ? { maxWidth: `calc(${width}px - 3rem)`, minWidth: '100px' }
                : undefined
            }
          >
            {getButtonText()}
          </span>

          <StaticIcon
            name='chevronDown'
            className={classNames(iconClassName, { 'rotate-180': isMenuOpen })}
            size={size === 'sm' ? 15 : undefined}
            onClick={() => setIsMenuOpen(prev => !prev)}
          />
        </button>
      )}

      {trigger && <button {...triggerProps}>{trigger}</button>}

      <ul
        id={id ? id + '-listbox' : undefined}
        role='listbox'
        aria-expanded={isMenuOpen}
        className={`absolute top-[calc(100%_+_theme(spacing.1))] z-50 max-h-[24rem] min-w-full flex-col overflow-y-auto overflow-x-hidden rounded bg-cool-50 shadow-menu ${contentClassName}
          ${isMenuOpen ? 'flex' : 'hidden'}
        `}
      >
        <ChildrenWithProps<ComponentProps<typeof Option>>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          only={Option as any}
          props={{
            onClick: event => {
              const newValue = event.currentTarget.getAttribute('data-value')
              if (!newValue) return
              newValue && setInternalValue(newValue)
              onChange && onChange(newValue)
              setIsMenuOpen(false)
            },
            size,
            className: tw`border-b border-cool-400`,
            isSelected: val => val === internalValue,
          }}
        >
          {children}
        </ChildrenWithProps>
      </ul>
    </div>
  )
}
